import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarPlusIcon, EyeIcon, EyeOffIcon, LockIcon, MailIcon, PencilIcon, PhoneIcon, TriangleAlertIcon } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { PersonalDataModal } from '../../components/shared/PersonalDataModal';
import { attendanceSummary } from '../../utils/attendance';
import { missingFields } from '../../utils/personal';
import { documentTypeLabel, eventTitle, formatDate, functionLabel, maskiraj, memberTypeClass, memberTypeLabel, personalFieldLabel, voiceClass, voiceLabel } from '../../utils/labels';
export function MemberProfile() {
  const {
    currentMember,
    settings,
    fees,
    upcoming,
    isExpected
  } = useApp();
  const me = currentMember!;
  const povzetek = attendanceSummary(me, settings);
  const jeGost = me.type === 'gostujoci' || me.type === 'castni';
  const clanarina = fees.find(f => f.memberId === me.id && f.leto === settings.letoClanarine);
  const [odprto, setOdprto] = useState(false);
  const [urejanje, setUrejanje] = useState(false);

  /** Potovalni dogodki, za katere član še ni dopolnil zahtevanih podatkov. */
  const manjkaZaPot = useMemo(() => {
    return upcoming.filter(e => e.potovanje?.aktivno && isExpected(e, me.id)).map(e => ({
      e,
      manjka: missingFields(me, e.potovanje!.obveznaPolja)
    })).filter(x => x.manjka.length > 0);
  }, [upcoming, isExpected, me]);
  const zasebno = [{
    label: personalFieldLabel.naslov,
    value: me.naslov ? `${me.naslov}, ${me.postnaStevilka ?? ''} ${me.posta ?? ''}`.trim() : undefined
  }, {
    label: personalFieldLabel.datumRojstva,
    value: me.datumRojstva ? formatDate(me.datumRojstva) : undefined
  }, {
    label: personalFieldLabel.dokumentTip,
    value: me.dokumentTip ? documentTypeLabel[me.dokumentTip] : undefined
  }, {
    label: personalFieldLabel.dokumentStevilka,
    value: me.dokumentStevilka,
    maskirano: true
  }, {
    label: personalFieldLabel.prehrana,
    value: me.prehrana
  }];
  return <div className="mx-auto max-w-3xl space-y-4" data-id="element-1705" data-mp="1wphux1amlybd">
      <h1 className="text-2xl font-semibold text-ink" data-id="element-1706" data-mp="2aho8qgqb3t7n">Moj profil</h1>

      <Card className="p-5" data-id="element-1707" data-mp="2afv3n5vcdh1r">
        <div className="flex items-center gap-4" data-id="element-1708" data-mp="26g31bawjk74u">
          <Avatar member={me} size="lg" data-id="element-1709" data-mp="26fb8g7ubwni9" />
          <div data-id="element-1710" data-mp="2kfuamyc44zci">
            <h2 className="text-lg font-semibold text-ink" data-id="element-1711" data-mp="2kfm0vfbaea5j">
              {me.ime} {me.priimek}
            </h2>
            <p className="text-[13px] text-ink-muted" data-id="element-1712" data-mp="2fgho6ep5luag">Član/ica od {formatDate(me.clanOd)}</p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-3" data-id="element-1713" data-mp="q2mcawoxi5m4">
          <div data-id="element-1714" data-mp="l2zzgod0irdu">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1715" data-mp="l3jhs66krhse">Tip članstva</p>
            <div className="mt-1.5" data-id="element-1716" data-mp="2wr4s01ar82ls">
              <Badge className={memberTypeClass[me.type]} data-id="element-1717" data-mp="2ws6hhasiorn1">{memberTypeLabel[me.type]}</Badge>
            </div>
          </div>
          <div data-id="element-1718" data-mp="2n0a4yupooyza">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1719" data-mp="2n0tqelejm5i2">Glasovna skupina</p>
            <div className="mt-1.5" data-id="element-1720" data-mp="2rylf76jw5bi1">
              <Badge className={voiceClass[me.voice]} data-id="element-1721" data-mp="2rxk637mfqz2y">{voiceLabel[me.voice]}</Badge>
            </div>
          </div>
          <div data-id="element-1722" data-mp="2e1yj15zripd6">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted" data-id="element-1723" data-mp="2iyf6dxz50zp7">Funkcije v zboru</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5" data-id="element-1724" data-mp="241xtv3f7pf12">
              {me.functions.length === 0 ? <span className="text-[13px] text-ink-muted" data-id="element-1725" data-mp="28zpnc1vi997d">Brez funkcije</span> : me.functions.map(f => <Badge key={f} data-id="element-1726" data-mp="2bclbreo18wc">{functionLabel[f]}</Badge>)}
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-2 border-t border-line pt-4 text-sm text-ink-muted sm:grid-cols-2" data-id="element-1727" data-mp="78csk5n94hp1">
          <p className="flex items-center gap-2" data-id="element-1728" data-mp="23a6ffj93dm6w">
            <MailIcon className="h-4 w-4" data-id="element-1729" data-mp="1zafggs9evzra" />
            {me.email}
          </p>
          <p className="flex items-center gap-2" data-id="element-1730" data-mp="1ub2yakthjgv1">
            <PhoneIcon className="h-4 w-4" data-id="element-1731" data-mp="1u9rj7oulamom" />
            {me.telefon}
          </p>
        </div>
      </Card>

      {manjkaZaPot.length > 0 && <Card className="border-amber-200 bg-amber-50/60 p-4" data-id="element-1732" data-mp="1kd6g5924rlnf">
          <div className="flex items-start gap-3" data-id="element-1733" data-mp="1kfj5w8e4r0b1">
            <TriangleAlertIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" data-id="element-1734" data-mp="1fmg5bgaxqqlc" />
            <div className="flex-1" data-id="element-1735" data-mp="1bkl7ils7qvcb">
              <p className="text-sm font-medium text-ink" data-id="element-1736" data-mp="1blmjqtkytnvm">
                Za {eventTitle(manjkaZaPot[0].e)} morate dopolniti osebne podatke.
              </p>
              <p className="mt-0.5 text-[13px] text-ink-muted" data-id="element-1737" data-mp="11ngk6immsttx">
                Manjka:{' '}
                {manjkaZaPot[0].manjka.map(p => personalFieldLabel[p].toLowerCase()).join(', ')}.
              </p>
              <Button size="sm" className="mt-3" onClick={() => setUrejanje(true)} data-id="element-1738" data-mp="mkotbb74ew6b">
                Dopolni osebne podatke
              </Button>
            </div>
          </div>
        </Card>}

      <Card className="p-5" data-id="element-1739" data-mp="whtkxqjqjjui">
        <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1740" data-mp="wimh5xl2iqye">
          <div data-id="element-1741" data-mp="11g4sfumk5i9k">
            <div className="flex items-center gap-2" data-id="element-1742" data-mp="11gnzuhel0o9c">
              <h2 className="text-base font-semibold text-ink" data-id="element-1743" data-mp="3tnymzd19msk">Osebni podatki</h2>
              <Badge className="bg-slate-100 text-ink-muted border-line" data-id="element-1744" data-mp="8sieptjm8x8z">
                <LockIcon className="h-3 w-3" data-id="element-1745" data-mp="8ss5hjchj8fq" />
                Zasebno
              </Badge>
            </div>
            <p className="mt-0.5 text-sm text-ink-muted" data-id="element-1746" data-mp="dpimc8w46827">
              Podatke uporabljamo za organizacijo gostovanj. Drugi člani jih ne vidijo.
            </p>
          </div>
          <div className="flex gap-2" data-id="element-1747" data-mp="3h7sntllxb2q9">
            <Button variant="secondary" size="sm" onClick={() => setOdprto(v => !v)} data-id="element-1748" data-mp="3m7f1fw5nykzl">
              {odprto ? <EyeOffIcon className="h-4 w-4" data-id="element-1749" data-mp="3e6pitr0zu15t" /> : <EyeIcon className="h-4 w-4" data-id="element-1750" data-mp="3e5nvoo6pdo7q" />}
              {odprto ? 'Skrij' : 'Pokaži'}
            </Button>
            <Button size="sm" onClick={() => setUrejanje(true)} data-id="element-1751" data-mp="396uu9u4oxnnx">
              <PencilIcon className="h-4 w-4" data-id="element-1752" data-mp="3nx211clc5i8s" />
              Uredi podatke
            </Button>
          </div>
        </div>

        {odprto ? <dl className="mt-4 divide-y divide-line border-t border-line" data-id="element-1753" data-mp="3ixxqoimodegv">
            {zasebno.map(v => <div key={v.label} className="flex flex-wrap items-center gap-2 py-2.5" data-id="element-1754" data-mp="2v9xrcubasfqo">
                <dt className="w-56 shrink-0 text-[13px] text-ink-muted" data-id="element-1755" data-mp="2qal62e02rgq7">{v.label}</dt>
                <dd className="text-sm text-ink" data-id="element-1756" data-mp="356ss7jv1ms84">
                  {v.value ? v.maskirano ? <span className="font-mono" data-id="element-1757" data-mp="3090yqler2y1t">{maskiraj(v.value)}</span> : v.value : <span className="text-amber-700" data-id="element-1758" data-mp="ffxk97wrph5d">Ni vpisano</span>}
                </dd>
              </div>)}
          </dl> : <p className="mt-4 rounded-lg bg-canvas px-4 py-3 text-[13px] text-ink-muted" data-id="element-1759" data-mp="rzuiph9xzmm0">
            Naslov, datum rojstva, podatki o dokumentu in posebnosti pri prehrani so skriti. Kliknite
            „Pokaži“ za pregled.
          </p>}
      </Card>

      <div className="grid gap-4 sm:grid-cols-2" data-id="element-1760" data-mp="i2yh7vigo0qy">
        <Card className="p-5" data-id="element-1761" data-mp="15rr3t1d70w8w">
          <div className="flex items-start justify-between gap-3" data-id="element-1762" data-mp="15rhnk5hya3ge">
            <h2 className="text-base font-semibold text-ink" data-id="element-1763" data-mp="1aorclk1fgd3p">Prisotnost</h2>
            {!jeGost && <StatusBadge status={povzetek.status} odstotek={povzetek.odstotek} data-id="element-1764" data-mp="wz7eirisk40q" />}
          </div>
          <p className="mt-2 text-sm text-ink" data-id="element-1765" data-mp="10z7rs8u5m967">
            {povzetek.prisoten} od {povzetek.pricakovan} dogodkov v sezoni 2026/27.
          </p>
          <Link to="/clan/prisotnost" className="mt-2 inline-block text-[13px] font-medium text-green-600 hover:underline" data-id="element-1766" data-mp="1ooafnyfdsy95">
            Podroben pregled
          </Link>
        </Card>

        <Card className="p-5" data-id="element-1767" data-mp="14rh31dybgeg3">
          <div className="flex items-start justify-between gap-3" data-id="element-1768" data-mp="zyo08vmerg6n">
            <h2 className="text-base font-semibold text-ink" data-id="element-1769" data-mp="uzulkzvdxj4w">Članarina {settings.letoClanarine}</h2>
            <Badge className="bg-green-50 text-green-700 border-green-200" dot="bg-green-500" data-id="element-1770" data-mp="q0rsmdsoj3da">
              Poravnano
            </Badge>
          </div>
          <p className="mt-2 text-sm text-ink" data-id="element-1771" data-mp="1nn7c1l4o2b3h">
            {clanarina?.znesek ?? settings.znesekClanarine} € · plačano{' '}
            {clanarina?.datumPlacila ? formatDate(clanarina.datumPlacila) : '—'}
          </p>
          <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1772" data-mp="1dohslnx4gil4">Za leto 2027 boste položnico prejeli januarja.</p>
        </Card>
      </div>

      <Card className="p-5" data-id="element-1773" data-mp="2axgxzk3jkum">
        <h2 className="text-base font-semibold text-ink" data-id="element-1774" data-mp="2c8idsq2kgik">Odsotnosti</h2>
        <p className="mt-1 text-[13px] text-ink-muted" data-id="element-1775" data-mp="3thqf0ujlf3ux">
          Sporočite daljšo odsotnost vnaprej, da vodstvo lažje načrtuje vaje.
        </p>
        <Link to="/clan/odsotnost" className="mt-3 inline-flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-[13px] font-medium text-ink transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-1776" data-mp="2910ryw0294t7">
          <CalendarPlusIcon className="h-4 w-4" data-id="element-1777" data-mp="2no2djab76hnm" />
          Prijavi daljšo odsotnost
        </Link>
      </Card>

      <PersonalDataModal member={me} open={urejanje} onClose={() => setUrejanje(false)} data-id="element-1778" data-mp="2snyjhf3ggj5t" />
    </div>;
}